/**
 * @author Valentin Hervieu
 * https://codesandbox.io/s/y09komm059?file=/src/canvasUtils.js
 */

// Function to load an image and return it as a Promise
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image)); // Image loaded successfully
    image.addEventListener("error", (error) => {
      console.error("Error loading image:", error); // Log image load error
      reject(error); // Reject promise if image fails to load
    });
    image.setAttribute("crossOrigin", "anonymous"); // Handle CORS issues
    image.src = url;
  });

// Convert degrees to radians
function getRadianAngle(degreeValue) {
  return (degreeValue * Math.PI) / 180;
}

/**
 * This function generates a cropped and rotated image based on the provided pixelCrop and rotation.
 * @param {string} imageSrc - The source URL of the image to crop
 * @param {Object} pixelCrop - Pixel crop dimensions (x, y, width, height)
 * @param {number} rotation - Optional rotation in degrees
 * @returns {Promise<Blob>} - The cropped image as a Blob
 */
export async function getCroppedImg(imageSrc, pixelCrop, rotation = 0) {
  // Load image
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  // Calculate safe area for the canvas based on image size and rotation
  const maxSize = Math.max(image.width, image.height);
  const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2));

  // Set canvas size to the safe area
  canvas.width = safeArea;
  canvas.height = safeArea;

  // Translate context to allow rotation around the image center
  ctx.translate(safeArea / 2, safeArea / 2);
  ctx.rotate(getRadianAngle(rotation));
  ctx.translate(-safeArea / 2, -safeArea / 2);

  // Draw image to canvas with correct offsets
  ctx.drawImage(
    image,
    safeArea / 2 - image.width * 0.5,
    safeArea / 2 - image.height * 0.5
  );

  // Get image data for the rotated image
  const data = ctx.getImageData(0, 0, safeArea, safeArea);

  // Set canvas size to the pixel crop dimensions
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  // Paste the cropped and rotated image onto the final canvas
  ctx.putImageData(
    data,
    Math.round(0 - safeArea / 2 + image.width * 0.5 - pixelCrop.x),
    Math.round(0 - safeArea / 2 + image.height * 0.5 - pixelCrop.y)
  );

  // Return the image as a Blob
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob); // Return the Blob if successful
      } else {
        console.error("Failed to generate blob"); // Log error if Blob generation fails
        reject(new Error("Failed to generate blob"));
      }
    }, "image/jpeg");
  });
}

/**
 * This function rotates the image without cropping.
 * @param {string} imageSrc - The source URL of the image to rotate
 * @param {number} rotation - The rotation angle in degrees
 * @returns {Promise<string>} - The rotated image as a Blob URL
 */
export async function getRotatedImage(imageSrc, rotation = 0) {
  // Load image
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  // Adjust canvas size based on rotation angle
  const orientationChanged =
    rotation === 90 ||
    rotation === -90 ||
    rotation === 270 ||
    rotation === -270;
  if (orientationChanged) {
    canvas.width = image.height;
    canvas.height = image.width;
  } else {
    canvas.width = image.width;
    canvas.height = image.height;
  }

  // Rotate the canvas context around the center of the image
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.drawImage(image, -image.width / 2, -image.height / 2);

  // Return the rotated image as a Blob URL
  return new Promise((resolve, reject) => {
    canvas.toBlob((file) => {
      if (file) {
        console.log("Rotated image blob:", file); // Debug log to check blob content
        resolve(URL.createObjectURL(file)); // Return the rotated image as a Blob URL
      } else {
        console.error("Failed to generate rotated image");
        reject(new Error("Failed to generate rotated image"));
      }
    }, "image/png");
  });
}
